<template>
<page-wrapper class="jobs">
  <v-row dense class="top-controls">
    <v-text-field class="job-search" v-model="searchText"
                  label="Search jobs" placeholder="Search by number, name, client or description"
                  clearable hide-details autocomplete="off"></v-text-field>
    <job-status-select v-if="user.admin" class="filter-select" v-model="statusFilter"
                       clearable hide-details></job-status-select>
    <v-select v-if="user.admin" class="filter-select" v-model="managerFilter" :items="availableManagers"
              item-text="name" item-value="id"
              label="Manager" clearable hide-details>
    </v-select>
    <v-spacer></v-spacer>
    <v-btn-toggle v-if="user.admin" v-model="view" mandatory dense>
      <v-btn value="default" small>Default</v-btn>
      <v-btn value="online" small>Online</v-btn>
    </v-btn-toggle>
    <v-btn v-if="user.admin" color="primary" depressed @click="newJob">New job</v-btn>
  </v-row>

  <v-dialog v-model="editing" max-width="640px">
    <edit-job ref="editor" @close="onClose"></edit-job>
  </v-dialog>
  <v-data-table :headers="headers" :items="filteredJobs"
                :items-per-page="50" :footer-props="{ itemsPerPageOptions: [25, 50, 100, -1] }"
                :expanded="expandedJobs" item-key="id" :loading="loading"
                elevation="0" class="job-table">
    <template v-slot:item.number="{ item }">
      <strong>{{ item.number }}</strong>
    </template>
    <template v-slot:item.name="{ item }">
      <span v-if="user.admin" class="clickable" @click="editJob(item)">{{ item.name }}</span>
      <span v-else>{{ item.name }}</span>
    </template>
    <template v-slot:item.edit="{ item }">
      <v-icon small @click="editJob(item)">mdi-pencil</v-icon>
    </template>
    <template v-slot:item.load-list="{ item }">
      <div v-if="user.admin">
        <v-icon small :color="loadListColour(item.load_list_status)"
                @click="$router.push({ name: 'load-list', params: { jobId: item.id } })">
          mdi-clipboard-list
        </v-icon>
      </div>
      <v-btn v-else depressed @click="$router.push({ name: 'load-list', params: { jobId: item.id } })">Load List</v-btn>
    </template>
    <template v-slot:item.status="{ item }">
      <job-status-select v-if="user.admin" class="status-select" :value="item.status"
                         dense hide-details flat solo
                         @input="changeStatus(item, $event)"></job-status-select>
      <v-chip v-else small :color="statusColour(item.status)" dark>{{ item.status }}</v-chip>
    </template>
    <template v-slot:item.branch="{ item }">
      <span class="grey--text text--darken-2">{{ branchOptionsMap[item.branch] }}</span>
    </template>
    <template v-slot:item.on_socials="{ item }">
      <v-icon small :color="item.on_socials ? 'green' : 'grey lighten-2'">
        {{ item.on_socials ? 'mdi-check-circle' : 'mdi-circle-outline' }}
      </v-icon>
    </template>
    <template v-slot:item.on_imdb="{ item }">
      <v-icon small :color="item.on_imdb ? 'green' : 'grey lighten-2'">
        {{ item.on_imdb ? 'mdi-check-circle' : 'mdi-circle-outline' }}
      </v-icon>
    </template>
    <template v-slot:item.website="{ item }">
      <v-icon v-if="websiteProjectByJobId[item.id]" small color="indigo"
              @click="$router.push({ name: 'website-projects', params: { id: websiteProjectByJobId[item.id].id } })">
        mdi-web
      </v-icon>
      <v-icon v-else small color="grey lighten-2">
        mdi-circle-outline
      </v-icon>
    </template>
<!--    <template v-slot:expanded-item="{ headers, item }">-->
<!--      <td :colspan="headers.length" class="description-row">{{ item.description }}</td>-->
<!--    </template>-->
  </v-data-table>
</page-wrapper>
</template>

<script>
import { mapState } from 'vuex'
import * as api from '../api'
import PageWrapper from '../components/ui/PageWrapper'
import EditJob from '../components/jobs/EditJob'
import JobStatusSelect from '../components/jobs/JobStatusSelect'
import { jobStatuses, branchOptionsMap } from '../data'

const HEADERS = {
  default: [
    { text: 'Number', value: 'number', width: '100px' },
    { text: 'Name', value: 'name' },
    { text: 'Client', value: 'client' },
    { text: 'Status', value: 'status', width: '180px' },
    { text: 'Branch', value: 'branch', width: '90px' },
    { text: '', value: 'edit', sortable: false, width: '40px' },
    { text: '', value: 'load-list', sortable: false, width: '40px' }
  ],
  online: [
    { text: 'Number', value: 'number', width: '100px' },
    { text: 'Name', value: 'name' },
    { text: 'Client', value: 'client' },
    { text: 'Web', value: 'website', width: '80px' },
    { text: 'Socials', value: 'on_socials', width: '90px' },
    { text: 'IMDb', value: 'on_imdb', width: '80px' },
    { text: '', value: 'edit', sortable: false, width: '40px' }
  ]
}

const STATUS_COLOUR_MAP = Object.fromEntries(jobStatuses.map(s => [s.value, s.colour]))

export default {
  name: 'JobList',
  components: { PageWrapper, EditJob, JobStatusSelect },
  props: {
    editId: { type: Number, default: null }
  },
  data() {
    return {
      loading: true,
      editing: false,
      searchText: null,
      statusFilter: null,
      managerFilter: null,
      view: 'default',
      branchOptionsMap
    }
  },
  computed: {
    ...mapState(['jobs', 'user', 'websiteProjects']),
    headers() {
      if (!this.user.admin) {
        return HEADERS.default.filter(h => ['number', 'name', 'load-list'].includes(h.value))
      }
      return HEADERS[this.view] || HEADERS.default
    },
    websiteProjectByJobId() {
      const map = {}
      for (const p of this.websiteProjects) map[p.job] = p
      return map
    },
    availableManagers() {
      const seen = new Set()
      const managers = this.jobs
        .map(j => j.manager)
        .filter(m => m && m.id && !seen.has(m.id) && seen.add(m.id))
        .sort((a, b) => a.name.localeCompare(b.name))
      const me = managers.find(m => m.id === this.user.id)
      if (!me) return managers
      return [me, ...managers.filter(m => m.id !== this.user.id)]
    },
    expandedJobs() {
      return this.filteredJobs.filter(j => j.description && this.user.admin)
    },
    filteredJobs() {
      let result = this.jobs
      if (this.searchText) {
        result = result.filter(job => this.jobFilter(job, this.searchText))
      }
      if (this.statusFilter) {
        result = result.filter(job => job.status === this.statusFilter)
      }
      if (this.managerFilter) {
        result = result.filter(job => job.manager && job.manager.id === this.managerFilter)
      }
      return result
    }
  },
  methods: {
    jobFilter(item, queryText) {
      const fields = [
        String(item.number),
        item.name || '',
        item.client || '',
        item.description || ''
      ].join(' ').toLowerCase()
      return queryText.toLowerCase().split(/\s+/).filter(Boolean).every(word => fields.includes(word))
    },
    initials(name) {
      if (!name || name.length < 1) {
        return ''
      }
      return name.split(' ').map(w => w[0]).join('').toUpperCase()
    },
    statusColour(status) {
      return STATUS_COLOUR_MAP[(status || '').toLowerCase()] || 'grey'
    },
    async changeStatus(job, status) {
      if (status === job.status) return
      await api.patch(`/job/${job.id}/status`, { status })
      job.status = status
    },
    editJob(job) {
      this.$router.replace({ name: 'job-list', params: { id: job.id } }).catch(() => {})
      this.editing = true
      this.$nextTick(() => this.$refs.editor.setJob(job))
    },
    newJob() {
      this.editing = true
      this.$nextTick(() => this.$refs.editor.newJob())
    },
    onClose() {
      this.editing = false
      this.$router.replace({ name: 'job-list' }).catch(() => {})
    },
    loadListColour(status) {
      if (status === 'empty') {
        return '#777'
      } else if (status === 'partial') {
        return 'orange lighten-1'
      } else if (status === 'complete') {
        return 'green lighten-1'
      }
    }
  },
  async created() {
    await this.$store.dispatch('getJobs')
    this.loading = false
    this.$store.dispatch('getWebsiteProjects')
    if (this.editId) {
      const job = this.jobs.find(j => j.id === this.editId)
      if (job) this.editJob(job)
    }
  }
}
</script>

<style scoped lang="scss">
.top-controls {
  width: 100%;
  align-items: center;

  > * {
    margin: 5px 10px;
  }

  .job-search {
    max-width: 450px;
  }

  .filter-select {
    max-width: 180px;
  }
}

.job-table {
  margin-top: 8px;

  .clickable {
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  .status-select {
    max-width: 170px;

    ::v-deep .v-input__slot {
      padding: 0 8px !important;
    }

    ::v-deep .v-select__selections {
      flex-wrap: nowrap;
    }
  }
}
@media (max-width: 600px) {
  .desktop-only {
    display: none;
  }
}
</style>

<style>
div.jobs {
  .v-data-table-header-mobile {
    display: none;
  }

  td.v-data-table__mobile-row {
    height: unset !important;
    min-height: unset !important;
    padding: 2px 0 !important;
  }

  .v-data-table__expanded__content {
    box-shadow: none !important;
  }

  .v-data-table__expanded__row {
    td {
      border-bottom: none !important;
    }
  }

  .job-table {
    .description-row {
      padding-left: 115px;
      font-size: 0.85rem;
      color: #666;
      font-style: italic;
    }
  }
}
</style>
